/**
 * paletteShader.wgsl.ts — WGSL shader source for WebGPU palette-swap filter.
 *
 * # What this file owns (Layer 4b, Option B)
 *
 * A single WGSL source string containing BOTH vertex and fragment entry
 * points (`mainVertex` and `mainFragment`), as PixiJS v8.18's
 * `GpuProgram.from()` expects. WebGPU is increasingly the default
 * renderer in modern browsers (Chrome/Edge 2026+); without this WGSL
 * companion the filter would silently render nothing on those backends.
 *
 *   - Sister file:  paletteShader.glsl.ts  (GLSL — WebGL backend)
 *   - This file:    paletteShader.wgsl.ts  (WGSL — WebGPU backend)
 *
 * Per the v8 Filter constructor source, if a Filter omits the program
 * for the active renderer, the filter is silently skipped. We provide
 * both shader sources so the filter renders correctly under either
 * backend.
 *
 * # WGSL conventions (LOCKED from PixiJS v8.18 source)
 *
 * Verified by reading `colorMatrixFilter.wgsl.mjs` from PixiJS v8.18:
 *
 *   - Single source file with both `@vertex fn mainVertex` and
 *     `@fragment fn mainFragment` entry points. Referenced by name from
 *     `GpuProgram.from({ vertex: { entryPoint: 'mainVertex' }, fragment:
 *     { entryPoint: 'mainFragment' } })`.
 *
 *   - PixiJS reserves `@group(0)` for built-in filter uniforms. The
 *     standard bindings:
 *       @group(0) @binding(0) var<uniform> gfu : GlobalFilterUniforms;
 *       @group(0) @binding(1) var uTexture : texture_2d<f32>;
 *       @group(0) @binding(2) var uSampler : sampler;
 *
 *   - Custom filter uniforms go on `@group(1) @binding(0)`, naming the
 *     binding to match the JavaScript-side `resources` key in the Filter
 *     constructor. We use binding name `paletteUniforms` — must match
 *     `paletteFilter.ts` resource key.
 *
 *   - Vertex output struct: must include `@builtin(position) position`
 *     and at least `@location(0)` for fragment input. The `@location(0)`
 *     varying name in the fragment is `uv` (per PixiJS convention),
 *     NOT `vTextureCoord` like in GLSL.
 *
 *   - Texture sampling: `textureSample(uTexture, uSampler, uv)` — both
 *     texture and sampler bindings are required.
 *
 *   - Pre-multiplied alpha: same pattern as GLSL. Un-premultiply before
 *     transforms, re-premultiply on output. WGSL's `var<uniform>`
 *     bindings are otherwise no different in alpha semantics.
 *
 *   - Vec3 alignment: WGSL's std140-equivalent layout pads vec3 to vec4
 *     boundaries, so we use vec4 for the palette transform uniform. This
 *     also keeps the GLSL and WGSL uniform shapes structurally identical
 *     (both pass a single vec4 of (hueShift_radians, satScale,
 *     lightShift, contrast)).
 *
 * # Algorithm — identical to GLSL companion
 *
 * Same HSL hue-rotation pipeline, expressed in WGSL syntax. See
 * `paletteShader.glsl.ts` for full algorithm rationale and Option B
 * design discussion.
 *
 *   1. Sample texel via textureSample(uTexture, uSampler, uv).
 *   2. Early-out for transparent pixels (alpha < ε).
 *   3. Un-premultiply alpha.
 *   4. Convert RGB → HSL.
 *   5. Apply hueShift / satScale / lightShift / contrast.
 *   6. Convert HSL → RGB.
 *   7. Re-premultiply alpha and output.
 *
 * # Notes on WGSL specifics
 *
 *   - WGSL has no `mod()` for floats; use `(x - floor(x))` to wrap into
 *     0..1 range. Or use the WGSL builtin `fract()`. We use `fract()`
 *     for clarity.
 *
 *   - WGSL has no implicit type conversions; we explicitly use `f32`
 *     literal suffixes where ambiguity could matter, but for compound
 *     expressions WGSL infers cleanly.
 *
 *   - WGSL `select(false_value, true_value, condition)` is the WGSL
 *     equivalent of GLSL's `condition ? true_value : false_value`.
 *
 *   - WGSL output of fragment: `-> @location(0) vec4<f32>` — single-
 *     return-value form, no `out` declaration.
 *
 * # References
 *
 *   - WGSL spec: https://www.w3.org/TR/WGSL/
 *   - PixiJS v8.18 source verified: colorMatrixFilter.wgsl.mjs
 */

/**
 * Combined vertex + fragment WGSL source.
 *
 * Two `@vertex fn mainVertex` and `@fragment fn mainFragment` entry
 * points are co-located in the single source string per PixiJS v8
 * `GpuProgram.from()` convention.
 *
 * The vertex shader is functionally identical to PixiJS's default filter
 * vertex shader (filterVertexPosition + filterTextureCoord pattern),
 * adapted to WGSL syntax. Verbatim functional behavior preserved.
 *
 * The fragment shader implements the HSL hue-rotation palette-swap
 * algorithm matching the GLSL companion `paletteShader.glsl.ts`.
 */
export const paletteShaderWgsl = `struct GlobalFilterUniforms {
  uInputSize:vec4<f32>,
  uInputPixel:vec4<f32>,
  uInputClamp:vec4<f32>,
  uOutputFrame:vec4<f32>,
  uGlobalFrame:vec4<f32>,
  uOutputTexture:vec4<f32>,
};

struct PaletteUniforms {
  uPaletteTransform:vec4<f32>,
};

@group(0) @binding(0) var<uniform> gfu: GlobalFilterUniforms;
@group(0) @binding(1) var uTexture: texture_2d<f32>;
@group(0) @binding(2) var uSampler : sampler;
@group(1) @binding(0) var<uniform> paletteUniforms : PaletteUniforms;

struct VSOutput {
    @builtin(position) position: vec4<f32>,
    @location(0) uv : vec2<f32>,
};

fn filterVertexPosition(aPosition: vec2<f32>) -> vec4<f32>
{
    var position = aPosition * gfu.uOutputFrame.zw + gfu.uOutputFrame.xy;

    position.x = position.x * (2.0 / gfu.uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * gfu.uOutputTexture.z / gfu.uOutputTexture.y) - gfu.uOutputTexture.z;

    return vec4(position, 0.0, 1.0);
}

fn filterTextureCoord(aPosition: vec2<f32>) -> vec2<f32>
{
    return aPosition * (gfu.uOutputFrame.zw * gfu.uInputSize.zw);
}

@vertex
fn mainVertex(
    @location(0) aPosition : vec2<f32>,
) -> VSOutput {
    return VSOutput(
        filterVertexPosition(aPosition),
        filterTextureCoord(aPosition),
    );
}

// Convert RGB (0-1 range) to HSL (0-1 range for all components).
// Standard algorithm: L = (max + min) / 2, S derived from L, H derived
// from which channel dominates.
fn rgbToHsl(rgb: vec3<f32>) -> vec3<f32> {
    let maxC = max(max(rgb.r, rgb.g), rgb.b);
    let minC = min(min(rgb.r, rgb.g), rgb.b);
    let delta = maxC - minC;

    let L = (maxC + minC) * 0.5;

    var S = 0.0;
    if (delta > 0.0) {
        if (L < 0.5) {
            S = delta / (maxC + minC);
        } else {
            S = delta / (2.0 - maxC - minC);
        }
    }

    var H = 0.0;
    if (delta > 0.0) {
        if (maxC == rgb.r) {
            // (g - b) / delta wrapped to 0..6
            let raw = (rgb.g - rgb.b) / delta;
            H = raw - floor(raw / 6.0) * 6.0;
        } else if (maxC == rgb.g) {
            H = ((rgb.b - rgb.r) / delta) + 2.0;
        } else {
            H = ((rgb.r - rgb.g) / delta) + 4.0;
        }
        H = H / 6.0;
        if (H < 0.0) {
            H = H + 1.0;
        }
    }

    return vec3<f32>(H, S, L);
}

// Helper for hslToRgb: compute one RGB channel from H,S,L parameters.
fn hueToRgb(p: f32, q: f32, t_in: f32) -> f32 {
    var t = t_in;
    if (t < 0.0) { t = t + 1.0; }
    if (t > 1.0) { t = t - 1.0; }
    if (t < 1.0 / 6.0) { return p + (q - p) * 6.0 * t; }
    if (t < 1.0 / 2.0) { return q; }
    if (t < 2.0 / 3.0) { return p + (q - p) * (2.0 / 3.0 - t) * 6.0; }
    return p;
}

// Convert HSL (0-1 range) to RGB (0-1 range).
fn hslToRgb(hsl: vec3<f32>) -> vec3<f32> {
    let H = hsl.x;
    let S = hsl.y;
    let L = hsl.z;

    if (S == 0.0) {
        return vec3<f32>(L, L, L);
    }

    var q = 0.0;
    if (L < 0.5) {
        q = L * (1.0 + S);
    } else {
        q = L + S - L * S;
    }
    let p = 2.0 * L - q;

    let r = hueToRgb(p, q, H + 1.0 / 3.0);
    let g = hueToRgb(p, q, H);
    let b = hueToRgb(p, q, H - 1.0 / 3.0);

    return vec3<f32>(r, g, b);
}

@fragment
fn mainFragment(
    @location(0) uv: vec2<f32>,
) -> @location(0) vec4<f32> {
    var color = textureSample(uTexture, uSampler, uv);

    // Early-out for transparent pixels: skip transform, avoid divide-by-zero.
    if (color.a < 0.001) {
        return vec4<f32>(0.0, 0.0, 0.0, 0.0);
    }

    // Un-premultiply alpha (PixiJS textures are pre-multiplied alpha).
    let rgb = color.rgb / color.a;

    // RGB -> HSL.
    var hsl = rgbToHsl(rgb);

    // Apply transforms:
    //   uPaletteTransform.x = hueShift in radians (convert to 0-1 cycle
    //                         by dividing by 2*PI).
    //   uPaletteTransform.y = satScale
    //   uPaletteTransform.z = lightShift
    //   uPaletteTransform.w = contrast
    let transform = paletteUniforms.uPaletteTransform;
    let hueShiftCycles = transform.x / (2.0 * 3.14159265358979);
    hsl.x = fract(hsl.x + hueShiftCycles);
    hsl.y = clamp(hsl.y * transform.y, 0.0, 1.0);
    hsl.z = clamp((hsl.z - 0.5) * transform.w + 0.5 + transform.z, 0.0, 1.0);

    // HSL -> RGB.
    let newRgb = hslToRgb(hsl);

    // Re-premultiply alpha for output.
    return vec4<f32>(newRgb * color.a, color.a);
}
`;
